import { useState } from "react";

import {
  useAuditEvents,
  useCancel,
  useEvents,
  useJob,
  useMigration,
  useProfile,
  useRetry,
  useSetPreflightOverride,
} from "../../api/hooks";
import { describeApiError } from "../../api/errors";
import type { MigrationEvent, MigrationState } from "../../api/types";
import { RotateCw } from "lucide-react";
import { Banner, Button, ConfirmDialog, FailureCard, Spinner } from "../../components";
import { t, tDynamic } from "../../i18n/t";
import { type OverrideChoice, type PreflightFinding, PreflightIssues } from "./preflightIssues";

function payloadOf(event: MigrationEvent | undefined): Record<string, unknown> {
  const payload = (event as { payload?: unknown } | undefined)?.payload;
  return typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Round-2 review fix (HIGH): the panel used to render only the newest event's `note` -- a short label
 * like "destination dry run rejected"/"destination commit rejected" -- never the destination's OWN
 * error lines the worker redacted onto `payload["errors"]` (see `workflow/stages.py`'s `result.errors or
 * result.batch_status == "failed"` gates). Worse, once a rejected COMMIT batch is rolled back,
 * `stage_rollback` appends a further `rolled_back` event with no note and no errors at all -- `events[0]`
 * is newest, so neither the note nor the errors from the `rolling_back` transition that actually
 * recorded the rejection were shown. `events` arrives newest-first (06a's `useEvents`), so walking it in
 * order and taking the first non-empty `payload.errors` finds the rejection whether it sits on the
 * newest event (`failed`, a dry-run/verify rejection) or one step back (`rolled_back`, after a commit
 * rejection's rollback completed).
 */
const FAILURE_CHAIN_STATES = new Set(["failed", "cancelled", "rolling_back", "rolled_back"]);

/**
 * Review fix (MEDIUM): the walk stops at the first event outside the current failure/rollback chain,
 * the same bound as the CLI and `_collect_failure_evidence`. Otherwise an older rejection (e.g. the
 * `mapping` event `_remap_on_destination_rejection` writes with `errors`) was shown as the cause of a
 * later, unrelated failure.
 */
function destinationErrors(events: MigrationEvent[]): string[] {
  for (const event of events) {
    const toState = (event as { to_state?: unknown }).to_state;
    if (typeof toState !== "string" || !FAILURE_CHAIN_STATES.has(toState)) {
      break;
    }
    const errors = payloadOf(event).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.filter((value): value is string => typeof value === "string");
    }
  }
  return [];
}

/**
 * (critique C9) `stage_profile` writes a preflight BLOCK straight to `failed`, with `payload.issues` --
 * never through `rolling_back` first (there is nothing to roll back: profiling is before any write) -- so
 * this reads only the NEWEST event, and only when it is itself the `failed` transition. Anything that is not
 * an object (or carries neither `code` nor `check` plus a `message`) is skipped rather than crashing the
 * panel -- the same tolerance as the API's own `_preflight_issue_out` and the CLI's `_print_preflight`.
 */
function preflightFindings(events: MigrationEvent[]): PreflightFinding[] {
  const newest = events[0];
  if (newest === undefined || (newest as { to_state?: unknown }).to_state !== "failed") {
    return [];
  }
  const issues = payloadOf(newest).issues;
  if (!Array.isArray(issues)) {
    return [];
  }
  const out: PreflightFinding[] = [];
  for (const issue of issues) {
    if (typeof issue !== "object" || issue === null) continue;
    const rec = issue as Record<string, unknown>;
    const code = typeof rec.code === "string" ? rec.code : typeof rec.check === "string" ? rec.check : null;
    const message = typeof rec.message === "string" ? rec.message : null;
    if (code === null || message === null) continue;
    out.push({
      code,
      severity: typeof rec.severity === "string" ? rec.severity : "BLOCK",
      message,
      source_value: typeof rec.source_value === "string" ? rec.source_value : null,
      destination_value: typeof rec.destination_value === "string" ? rec.destination_value : null,
    });
  }
  return out;
}

/**
 * §B6.7. **This panel owns every query and every mutation.** `components/FailureCard.tsx` (task 06a) is a
 * props-only primitive: it renders what it is given and calls nothing. That split is the resolution of
 * v2-feasibility-3 and must not be blurred by moving a hook into the card — the card takes an `actions` prop, so
 * the two buttons below are still this file's.
 */
export function FailurePanel({ migrationId }: { migrationId: string }): JSX.Element {
  const migration = useMigration(migrationId);
  const state = migration.data?.state as MigrationState | undefined;
  const events = useEvents(migrationId, state);
  const audits = useAuditEvents(migrationId, { limit: 1 });
  const retry = useRetry(migrationId);
  const cancel = useCancel(migrationId);
  const setOverride = useSetPreflightOverride(migrationId);
  // StatusFailed.html: Retry is a direct action (the card already says what a retry restarts from), and the
  // cancel is the inline aside card — reason field + danger-solid confirm — not a dialog.
  const [retrying, setRetrying] = useState(false);
  const [reason, setReason] = useState("");
  const [override, setPendingOverride] = useState<OverrideChoice | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // 06a's `useEvents` returns the accumulated list **newest first**, so the newest is index 0.
  const newest: MigrationEvent | undefined = events.events[0];
  const payload = payloadOf(newest);
  const errors = destinationErrors(events.events);
  const payloadFindings = preflightFindings(events.events);
  // The stored report also carries the WARNs and both sides' values, which the `failed` event's BLOCK-only
  // payload does not; it is read only for a preflight failure and the event's own list is the fallback.
  // Only a profiling-stage failure's stored report is the matching one: a LOAD/dry-run destination preflight
  // block (retry_target loading/dry_run) carries its own BLOCKs on the event, and the stored profiling
  // report is from the earlier run that PASSED -- showing it would hide the real BLOCKs.
  const retryTarget = asString(payload.retry_target);
  const profilingFailure = retryTarget === "profiling";
  const profile = useProfile(migrationId, payloadFindings.length > 0 && profilingFailure);
  const storedFindings: PreflightFinding[] = (profile.data?.issues ?? []).map((issue) => ({
    code: issue.check,
    severity: issue.severity,
    message: issue.message,
    source_value: issue.source_value ?? null,
    destination_value: issue.destination_value ?? null,
  }));
  const findings = profilingFailure && storedFindings.length > 0 ? storedFindings : payloadFindings;
  // xmg-orgadopt: the settings an empty LuciBook organisation would take from the source (or refused to).
  const adoptSettings = profilingFailure ? (profile.data?.adopt_settings ?? null) : null;
  // `payload["job_id"]` is the procrastinate BIGINT the failing event recorded; `useJob` takes a number.
  const rawJobId = payload.job_id;
  const jobId = typeof rawJobId === "number" ? rawJobId : null;
  const job = useJob(jobId);

  if (state === undefined) {
    return <Spinner />;
  }

  const rollingBack = state === "rolling_back";
  const requestId = asString(
    ((audits.data?.items?.[0] as { metadata?: Record<string, unknown> } | undefined)?.metadata ?? {})
      .request_id,
  );

  function focusCancelReason(): void {
    document.getElementById("failure-cancel-reason")?.focus();
  }

  return (
    <section data-testid="failure-panel" className="splitbody">
      <div className="splitbody__main">
        <FailureCard
          state={state}
          stateLabel={tDynamic("state.", state)}
          transitionLabel={
            typeof (newest as { from_state?: unknown } | undefined)?.from_state === "string"
              ? `${tDynamic("state.", (newest as { from_state: string }).from_state)} → ${tDynamic("state.", state)}`
              : undefined
          }
          /* The timestamp the state was entered is the newest event's own `created_at`. */
          enteredAt={newest?.created_at ?? null}
          /*
          §A3.2c: `runner.advance` swallows non-transient exceptions rather than re-raising them, so the event's
          redacted `note` is the ONLY place the stage's exception message survives.
        */
          note={newest?.note ?? null}
          retryable={typeof payload.retryable === "boolean" ? payload.retryable : null}
          /*
          (v2-L-2) `retry_target` is rendered VERBATIM by the card. `set_state` writes the state it failed from —
          which may be `mapping`, `dry_running`, `loading` or anything else — so "retrying will restart from
          extracting" is wrong in general and is not said anywhere.
        */
          retryTarget={retryTarget}
          requestId={requestId}
          /* (v2-BL-3) `deferredAt` is the job row's own column name; the invented alternative does not exist. */
          job={
            job.data === undefined
              ? null
              : {
                  id: String(job.data.job_id),
                  status: job.data.status,
                  statusLabel: tDynamic("job.", job.data.status),
                  attempts: job.data.attempts,
                  deferredAt: job.data.deferred_at ?? null,
                }
          }
          batchId={asString(payload.batch_id)}
          destinationErrors={errors}
          preflightSection={
            payloadFindings.length === 0 ? undefined : (
              <>
                {adoptSettings?.status === "refused" ? (
                  <Banner tone="warn">
                    {t("adopt.refused", {
                      destination: adoptSettings.destination_name,
                      code: adoptSettings.code ?? "",
                    })}
                  </Banner>
                ) : null}
                <PreflightIssues
                  adopt={adoptSettings?.status === "proposed" ? adoptSettings : null}
                  findings={findings}
                  sourceSystem={migration.data?.source_system ?? null}
                  destinationSystem={migration.data?.destination_system ?? null}
                  overridable={state === "failed" && retryTarget === "profiling"}
                  renderOverride={(choice) => (
                    <Button
                      key={choice.id}
                      onClick={() => {
                        setOverrideError(null);
                        setPendingOverride(choice);
                      }}
                    >
                      {choice.label()}
                    </Button>
                  )}
                />
                {overrideError === null ? null : <Banner tone="block">{overrideError}</Banner>}
              </>
            )
          }
          /* §B6.7: `rolling_back` offers no actions; the card itself hides this node in that state. */
          actions={
            rollingBack ? null : (
              <>
                <Button
                  variant="primary"
                  disabled={retrying}
                  onClick={() => {
                    setRetrying(true);
                    void retry.mutateAsync({}).finally(() => setRetrying(false));
                  }}
                >
                  <RotateCw size={16} strokeWidth={2} aria-hidden="true" />
                  {t("failure.retry")}
                </Button>
                <Button variant="destructive" onClick={focusCancelReason}>
                  {t("failure.cancel")}
                </Button>
              </>
            )
          }
        />
      </div>

      {/* StatusFailed.html's aside: the cancel is an inline card — the reason is written to the migration
          event and cannot be edited later, and the confirm is danger-solid, disabled until there is one. */}
      {rollingBack ? null : (
        <aside className="splitbody__aside">
          <section className="card cancelcard" aria-labelledby="failure-cancel-title">
            <h2 id="failure-cancel-title">{t("failure.cancel")}</h2>
            <label className="field" htmlFor="failure-cancel-reason">
              <span className="field__label">{t("failure.cancel_reason")}</span>
              <textarea
                id="failure-cancel-reason"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <span className="field__hint">{t("failure.cancel_reason_hint")}</span>
            </label>
            <Button
              variant="danger"
              disabled={reason.trim() === "" || cancel.isPending}
              onClick={() => void cancel.mutateAsync({ reason })}
            >
              {t("failure.cancel_confirm")}
            </Button>
          </section>
        </aside>
      )}

      <ConfirmDialog
        open={override !== null}
        title={t("preflight.override_title")}
        onCancel={() => setPendingOverride(null)}
        onConfirm={() => {
          const choice = override;
          if (choice === null) return;
          // The actor is written by the server from the signed-in operator; the client sends only the choice.
          void setOverride
            .mutateAsync(choice.body)
            .then(() => retry.mutateAsync({}))
            .then(() => setPendingOverride(null))
            .catch((caught: unknown) => {
              setPendingOverride(null);
              setOverrideError(describeApiError(caught, t("error.unexpected")));
            });
        }}
      >
        {override === null ? null : (
          <p data-testid="override-recording">
            {t("preflight.override_recording", { override: override.label() })}
          </p>
        )}
        <p>{t("preflight.override_body")}</p>
      </ConfirmDialog>
    </section>
  );
}
